<script lang="ts">
  import { fly } from "svelte/transition";
  import { createEventDispatcher, getContext } from "svelte";
  import { getMediaQuery } from "$utils/mediaQuery";
  import { v4 as uuidv4 } from "uuid";
  import { defaultDocKey } from "./Gallery.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Cell from "./Cell.svelte";
  import cx from "classnames";
  import type { ItemsRow } from "./DocsGalleryBlock";
  import type { Document } from "app/models/documents";
  import type { Writable } from "svelte/store";
  import { Plus } from "$components/+blocks/OVIcon";

  export let row: ItemsRow;
  export let isEditable: boolean = false;

  const ROW_ANIMATION_MS = 200;
  const ROW_ANIMATION_DELAY_MS = 300;
  const ITEMS_LIMIT = 3;

  const defaultDoc = getContext<Writable<Document[]>>(defaultDocKey);

  let isAddItemVisible: boolean = false;
  let isEditingItem: boolean = false;
  let isDesktop = getMediaQuery("desktop");
  const dispatch = createEventDispatcher();

  // The cell width is calculated based on the number of items in the row, the gap between them and the container width.
  // This computed width allows to animate the cell width when adding or removing items.
</script>

<div
  class="relative -mx-1 flex items-center overflow-x-clip px-1"
  role="group"
  on:mouseenter={() => {
    if (isEditable && row.items.length < ITEMS_LIMIT) {
      isAddItemVisible = true;
    }
  }}
  on:mouseleave={() => {
    if (!isEditingItem) {
      isAddItemVisible = false;
    }
  }}
>
  <div
    class="grid grid-cols-[repeat(auto-fit,_minmax(200px,1fr))] gap-4 transition-[width] duration-300 lg:block lg:h-[192px] lg:gap-0 lg:space-x-4"
    class:w-[calc(100%-54px)]={isAddItemVisible}
    class:w-full={!isAddItemVisible || !isEditable}
  >
    {#each row.items as doc, index}
      <div
        in:fly={{
          x: row.items.length === 0 ? 0 : 125,
          duration: row.items.length > 1 ? ROW_ANIMATION_MS : 0,
          delay: row.items.length > 1 ? ROW_ANIMATION_DELAY_MS : 0,
        }}
        class="inline-block h-[192px] transition-[width] duration-300 lg:h-full"
        style={$isDesktop
          ? `width: calc((100% - (16px*(${row.items.length} - 1)))/${row.items.length})`
          : "width: 100%"}
      >
        <Cell
          item={doc}
          {isEditable}
          on:editing-item={(event) => (isEditingItem = event.detail)}
          on:delete-item={(event) =>
            dispatch("update-row", {
              ...row,
              items: row.items.filter((d) => d.cellUUID !== event.detail),
            })}
          on:update-item={(event) => {
            row.items[index] = event.detail;
            dispatch("update-row", {
              ...row,
              items: row.items,
            });
          }}
        />
      </div>
    {/each}
  </div>

  {#if isEditable}
    <button
      class={cx(
        "z-button absolute right-0 bottom-0 mr-1 flex h-full items-center rounded-md border border-black/10 p-2 dark:border-white/10",
        "bg-gray-50 transition-[transform,opacity,colors] duration-300 hover:bg-gray-100 dark:bg-neutral-900 dark:hover:bg-gray-700",
        {
          "translate-x opacity-100 disabled:opacity-50 disabled:delay-150": isAddItemVisible,
        },
        { "translate-x-full opacity-0": !isAddItemVisible }
      )}
      disabled={row.items.length === ITEMS_LIMIT}
      on:click={() =>
        dispatch("update-row", {
          ...row,
          items: [...row.items, { ...$defaultDoc, cellUUID: uuidv4() }],
        })}
    >
      <OVIcon icon={Plus} size="w-5" class="text-gray-500 dark:text-gray-400" />
    </button>
  {/if}
</div>
